import { clearStudentFiltersIfSessionChanged } from '../constants/course-catalog-constants';

/**
 * Context for Student application when embedded in a student portal.
 * The host may inject CP_STUDENT_CONTEXT or reuse CP_CONTEXT (same shape as Succeed).
 */

declare global {
  interface Window {
    CP_STUDENT_CONTEXT?: {
      API_URL?: string;
      DISTRICT_ID?: string;
      DISTRICT_NAME?: string;
      HIGHSCHOOL_ID?: string;
      BASENAME?: string;
      /** Login-lifetime identifier from the student host, independent of JWT renewal. */
      LOGIN_SESSION_ID?: string;
      /** Normalized host session envelope: `{ userSession, isValid }`. */
      USER_SESSION?: {
        userSession?: unknown;
        isValid?: boolean;
      };
      /** Role information derived by the host selectors. */
      ACTOR_CONTEXT?: {
        userType?: string;
        isParentUser?: boolean;
        isGhostingParentUser?: boolean;
        isViewAsStudent?: boolean;
        isStaffUser?: boolean;
      };
      /** Optional ghost actor metadata forwarded by the host for role resolution. */
      GHOST_ACTOR_ROLE?: string;
      GHOST_ACTOR_USER_ID?: string;
      /** True when host provides parent-navigation subscription for route synchronization. */
      HAS_HOST_NAVIGATION?: boolean;
      /** Student's current grade level (e.g. 7, 10). Injected by FCUI from userSession.grade. */
      GRADE?: number;
      /** Student's graduation class year (e.g. 2029). Injected by FCUI from userSession.classYear. */
      CLASS_YEAR?: number;
      /** Host-provided feature flag map for student MFE consumers. */
      CP_COURSE_PLANNER_FEATURES?: Record<string, unknown>;
      [key: string]: unknown;
    };
  }
}

/** Run before student routes mount so persisted filters cannot override a new login's default. */
export function synchronizeStudentCatalogSession(): void {
  if (typeof window === 'undefined' || !window.CP_STUDENT_CONTEXT) {
    return;
  }
  const context = window.CP_STUDENT_CONTEXT;
  const rawSession = context.USER_SESSION?.userSession;
  const session = typeof rawSession === 'object' && rawSession !== null ? rawSession : undefined;
  const hostSessionId =
    session && 'sessionId' in session && typeof session.sessionId === 'string'
      ? session.sessionId
      : undefined;
  const sessionId =
    context.LOGIN_SESSION_ID ||
    document.cookie.match(/(?:^|;\s*)SESSION=([^;]+)/)?.[1] ||
    hostSessionId;
  if (!sessionId) {
    return;
  }
  const studentId = session && 'id' in session ? session.id : undefined;
  clearStudentFiltersIfSessionChanged(
    JSON.stringify([sessionId, context.HIGHSCHOOL_ID, studentId, context.GHOST_ACTOR_USER_ID])
  );
}

/**
 * Returns true when the Student app is embedded in a host (student portal).
 * Checks CP_STUDENT_CONTEXT first, then CP_CONTEXT (student host may reuse same shape).
 */
export function isEmbeddedInStudentHost(): boolean {
  return typeof window !== 'undefined' && (!!window.CP_STUDENT_CONTEXT || !!window.CP_CONTEXT);
}

/**
 * Returns true when student MFE receives host route updates through the integration contract.
 * This distinguishes full host integration from standalone/local MFE runs.
 */
export function hasStudentHostNavigationIntegration(): boolean {
  return typeof window !== 'undefined' && window.CP_STUDENT_CONTEXT?.HAS_HOST_NAVIGATION === true;
}

/**
 * Student's enrolled grade from the host (e.g. FCUI userSession.grade).
 * Prefer this over inferring current grade from plan course statuses.
 */
export function getStudentGradeFromHost(): number | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const grade = window.CP_STUDENT_CONTEXT?.GRADE;
  return typeof grade === 'number' ? grade : undefined;
}

/**
 * Returns the router basename when embedded. Default: /course-planner-next
 */
export function getStudentEmbedBasename(): string {
  if (typeof window !== 'undefined' && window.CP_STUDENT_CONTEXT?.BASENAME) {
    return window.CP_STUDENT_CONTEXT.BASENAME;
  }
  return '/course-planner-next';
}
